import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { deletePlatformTenant, errorMessage } from "@/lib/api";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

/**
 * Platform-superadmin DANGER ZONE: irreversibly hard-delete ONE tenant.
 *
 * Distinct from Offboard (the soft action that only suspends a tenant) —
 * this permanently purges ALL of the tenant's data across every service and
 * cannot be undone. Guarded three ways:
 *   1. Rendered only for a P-SA operator (the parent gates on hasRole).
 *   2. The confirm button stays DISABLED until the operator types the
 *      tenant's EXACT companyName — a slip of the mouse can't delete.
 *   3. The backend route itself re-checks P-SA (require_platform_superadmin);
 *      the UI gate is convenience, not the security boundary.
 *
 * On confirm it calls DELETE /platform/tenants/:id (via deletePlatformTenant)
 * and renders the returned per-service deletion report (tables + counts, any
 * per-service errors, overall ok).
 */
export default function DangerZoneDeleteTenant({ tenant, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [status, setStatus] = useState("idle"); // idle | deleting | done | error
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  // A tenant with no resolved company name cannot be safely name-confirmed;
  // fall back to requiring the tenant id typed instead, so the guard still
  // forces a deliberate, exact match rather than silently allowing an
  // empty-string confirmation.
  const confirmTarget = tenant.company_name || tenant.tenant_id;
  const confirmLabel = tenant.company_name ? "company name" : "tenant id";
  const nameMatches = typed.trim() === confirmTarget;
  const busy = status === "deleting";

  const onConfirm = async () => {
    if (!nameMatches || busy) return;
    setStatus("deleting");
    setError("");
    setReport(null);
    try {
      const result = await deletePlatformTenant(tenant.tenant_id);
      setReport(result);
      setStatus("done");
      if (result?.ok) onDeleted?.(tenant.tenant_id);
    } catch (err) {
      const m = errorMessage(err);
      setError(m || "The delete could not be completed.");
      setStatus("error");
    }
  };

  return (
    <div
      className="rounded-md border border-destructive/40 bg-destructive/5 p-4"
      data-testid={`danger-zone-${tenant.tenant_id}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div>
          <p className="text-sm font-semibold text-destructive">Delete tenant (irreversible)</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Permanently purges <span className="font-medium">{tenant.company_name || tenant.tenant_id}</span> and
            all of its data across every service — sessions, credentials, roles, identities, SSO and the tenant
            record itself. This cannot be undone and is different from Offboard, which only suspends the tenant.
          </p>
        </div>
      </div>

      {status !== "done" && (
        <div className="mt-3 space-y-2">
          <label className="block text-xs font-medium" htmlFor={`confirm-${tenant.tenant_id}`}>
            Type the tenant's exact {confirmLabel} to enable deletion:{" "}
            <span className="font-mono text-destructive">{confirmTarget}</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              id={`confirm-${tenant.tenant_id}`}
              data-testid={`danger-zone-confirm-input-${tenant.tenant_id}`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmTarget}
              className="max-w-sm"
              autoComplete="off"
              disabled={busy}
            />
            <Button
              variant="destructive"
              size="sm"
              onClick={onConfirm}
              disabled={!nameMatches || busy}
              data-testid={`danger-zone-delete-btn-${tenant.tenant_id}`}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {busy ? "Deleting…" : "Delete tenant"}
            </Button>
          </div>
        </div>
      )}

      {status === "error" && (
        <p className="mt-3 text-sm text-destructive" data-testid={`danger-zone-error-${tenant.tenant_id}`}>
          {error}
        </p>
      )}

      {status === "done" && report && (
        <div className="mt-3" data-testid={`danger-zone-report-${tenant.tenant_id}`}>
          <div className="flex items-center gap-2">
            <Badge variant={report.ok ? "success" : "destructive"}>
              {report.ok ? "Deleted" : "Completed with errors"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {report.ok
                ? "Every service reported a clean deletion."
                : "Some services reported an error — review below and re-run if needed (purge is safe to repeat)."}
            </span>
          </div>
          <table className="mt-2 w-full text-xs" data-testid={`danger-zone-report-table-${tenant.tenant_id}`}>
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-1">Service</th>
                <th className="py-1">Result</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(report.services || {}).map(([service, outcome]) => (
                <tr key={service} className="border-b last:border-0 align-top">
                  <td className="py-1 font-medium">{service}</td>
                  <td className="py-1">
                    {outcome.error ? (
                      <span className="text-destructive">error: {outcome.error}</span>
                    ) : (
                      <span className="font-mono text-muted-foreground">
                        {Object.entries(outcome.deleted || {})
                          .map(([table, count]) => `${table}: ${count}`)
                          .join(", ") || "nothing to delete"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
